import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { describeError } from "@/api/client";
import type { Uuid } from "@/api/types";
import { Button, ButtonLink, Dialog, DialogClose, InlineAlert, NumberField, Select } from "@/components";
import { labelTemplatesQuery } from "@/features/settings/api";
import type { DecimalString } from "@/lib/decimal";
import { pluralize } from "@/lib/format";
import { useRenderLabels, type LabelItemKind, type RenderedLabels } from "../api";
import styles from "./PrintLabelsDialog.module.css";

const NOUNS: Record<LabelItemKind, [string, string]> = {
  lot: ["lot label", "lot labels"],
  location: ["location label", "location labels"],
  batch: ["batch label", "batch labels"],
};

/** Render a PDF sheet of labels (through the labels slice) and offer it to open and print at 100 %. */
export function PrintLabelsDialog({
  open,
  onOpenChange,
  itemKind,
  itemIds,
  subject,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemKind: LabelItemKind;
  itemIds: Uuid[];
  /** What is being labelled, for the title: "lot T030-24S", "3 locations". */
  subject: string;
}) {
  const templates = useQuery({ ...labelTemplatesQuery, enabled: open });
  const render = useRenderLabels();
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [startAt, setStartAt] = useState<DecimalString | null>("1");
  const [result, setResult] = useState<RenderedLabels | null>(null);
  const chosen = templateId ?? templates.data?.[0]?.id ?? null;
  const [one, many] = NOUNS[itemKind];

  function close(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setResult(null);
      render.reset();
    }
  }

  function print() {
    if (!chosen) return;
    render.mutate(
      {
        template_id: chosen,
        item_kind: itemKind,
        item_ids: itemIds,
        start_at: Math.max(1, Number.parseInt(startAt ?? "1", 10) || 1),
      },
      { onSuccess: setResult },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={`Print labels for ${subject}`}
      description={
        itemKind === "location"
          ? "Each QR code opens its location page and the lots stored there. Labels come as a PDF sheet. Print it at 100 % scale; calibration offsets from Settings are applied."
          : "Labels come as a PDF sheet. Print it at 100 % scale; calibration offsets from Settings are applied."
      }
      footer={
        result ? (
          <>
            <DialogClose>Done</DialogClose>
            <ButtonLink variant="primary" icon="externalLink" to={result.url} target="_blank" reloadDocument>
              Open the PDF
            </ButtonLink>
          </>
        ) : (
          <>
            <DialogClose />
            <Button
              variant="primary"
              icon="tag"
              onClick={print}
              pending={render.isPending}
              pendingLabel="Making the PDF…"
              disabled={!chosen || itemIds.length === 0}
            >
              {`Print ${pluralize(itemIds.length, one, many)}`}
            </Button>
          </>
        )
      }
    >
      <div className={styles.body}>
        {templates.isError ? (
          <InlineAlert tone="warning" title="Label templates couldn't be loaded">
            {describeError(templates.error)}
          </InlineAlert>
        ) : (
          <div className={styles.fields}>
            <Select
              label="Label sheet"
              options={(templates.data ?? []).map((template) => ({ value: template.id, label: template.label }))}
              value={chosen}
              onValueChange={setTemplateId}
              placeholder={templates.isPending ? "Loading templates…" : "Choose a sheet"}
            />
            <NumberField
              label="Start at label"
              description="Skip labels already used on a partly printed sheet."
              value={startAt}
              onValueChange={setStartAt}
              min={1}
              step={1}
              steppers
            />
          </div>
        )}
        {render.isError ? (
          <InlineAlert
            tone="danger"
            title="The labels couldn't be made"
            action={<Button onClick={print}>Retry</Button>}
          >
            {describeError(render.error)}
          </InlineAlert>
        ) : null}
        {result ? (
          <InlineAlert tone="success" title="Your labels are ready">
            Open the PDF and print it without scaling.
          </InlineAlert>
        ) : null}
      </div>
    </Dialog>
  );
}
